import { AnimatePresence, motion } from 'framer-motion';
import MarkdownIt from 'markdown-it';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AICard, Breakpoint, Button, MarkdownStyled, Paragraph, tokens } from '@treams/treams-ui';

import { useAIInsightsContext } from '../contexts/AIInsightsContext';
import { EvaluationAnswerAIInsightsLoadingState } from './EvaluationAnswerAIInsightsLoadingState';

const ANSWER_AVATAR_SIZE = 32;
const ANSWER_AVATAR_INDENT = ANSWER_AVATAR_SIZE + tokens.gap.element;

const Insights = styled.div<{ $side: 'left' | 'right' }>`
	margin-top: ${tokens.gap.group}px;
	${({ $side }) =>
		$side === 'right'
			? `margin-right: ${ANSWER_AVATAR_INDENT}px;`
			: `margin-left: ${ANSWER_AVATAR_INDENT}px;`}

	@media screen and (max-width: ${Breakpoint.Small}) {
		margin-left: 0;
		margin-right: 0;
	}
`;

export const DisclaimerText = styled(Paragraph)`
	font-size: ${tokens.typography.sizes.small}px;
	line-height: ${tokens.typography.lineHeights.small};
	color: ${tokens.text.secondary};
	margin-top: ${tokens.gap.element}px;
	text-decoration: underline;
`;

export const StreamingEvaluationAnswerAIInsights = ({
	id,
	autoOpen,
	side = 'left',
}: {
	id: string;
	autoOpen: boolean;
	side?: 'left' | 'right';
}) => {
	const {
		streamingInsightsData,
		insightsLoading,
		needsViewerApproval,
		aiInsightsEnabled,
		aiInsightsApproved,
		openAIInsightsApprovalModal,
	} = useAIInsightsContext();

	const [isOpen, setIsOpen] = useState(false);

	const { t } = useTranslation();

	const content = streamingInsightsData[id];

	const md = useMemo(() => new MarkdownIt(), []);
	const html = useMemo(() => (content ? md.render(content) : ''), [md, content]);

	useEffect(() => {
		if (autoOpen && content && !insightsLoading) {
			setIsOpen(true);
		}
	}, [autoOpen, content, insightsLoading, setIsOpen]);

	if (!aiInsightsEnabled) return null;

	return (
		<AnimatePresence>
			<Insights $side={side}>
				<motion.div key="ai-insights-wrapper">
					<AICard
						title={t('evaluation.submission.aiInsights.title')}
						count={content ? 1 : undefined}
						collapsed={!isOpen}
						onHeaderClick={() => setIsOpen(!isOpen)}
					>
						{needsViewerApproval && (
							<>
								{t('evaluation.submission.aiInsights.noPermission')}{' '}
								<Button
									onClick={openAIInsightsApprovalModal}
									type="inlineTextButton"
									title={t('evaluation.submission.aiInsights.openPermissionModal')}
								/>
							</>
						)}
						{!needsViewerApproval &&
							!aiInsightsApproved &&
							t('evaluation.submission.aiInsights.noPermissionFromEmployee')}
						{!needsViewerApproval && aiInsightsApproved && (
							<>
								{insightsLoading ? (
									<EvaluationAnswerAIInsightsLoadingState />
								) : content ? (
									<MarkdownStyled>
										<div dangerouslySetInnerHTML={{ __html: html }} />
									</MarkdownStyled>
								) : (
									t('evaluation.submission.aiInsights.noInsightsFound')
								)}
							</>
						)}
					</AICard>
				</motion.div>
				{!needsViewerApproval && aiInsightsApproved && (
					<motion.div
						key="ai-insights-disclaimer"
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: isOpen ? 1 : 0, height: isOpen ? 'auto' : 0 }}
						transition={{ duration: 0.2, ease: 'easeInOut' }}
						style={{ overflow: 'hidden' }}
					>
						<DisclaimerText>
							<a href={t('ai.disclaimer.link.to')} target="_blank" rel="noopener noreferrer">
								{t('evaluation.submission.aiInsights.disclaimer')}
							</a>
						</DisclaimerText>
					</motion.div>
				)}
			</Insights>
		</AnimatePresence>
	);
};
